import { useSyncExternalStore } from "react";
import { Editor } from "@tiptap/react";
import { ReactRenderer } from "@tiptap/react";
import { SuggestionKeyDownProps } from "@tiptap/suggestion";
import { VariableDef } from "../variable-input";
import VariableCommand, { VariableCommandRef } from "./variable-command";

// Only one @-popup can be open at a time, so its open state lives at module
// scope alongside the renderer it belongs to.
let activeComponent: ReactRenderer | null = null;
let isPopupOpen = false;
const subscribers = new Set<() => void>();

const setPopupOpen = (open: boolean) => {
  if (isPopupOpen === open) return;
  isPopupOpen = open;
  subscribers.forEach((notify) => notify());
};

export const isVariableSuggestionOpen = () => isPopupOpen;

/** Reactive form of `isVariableSuggestionOpen`, for UI that must yield to the popup. */
export const useVariableSuggestionOpen = () =>
  useSyncExternalStore(
    (notify) => {
      subscribers.add(notify);
      return () => subscribers.delete(notify);
    },
    isVariableSuggestionOpen,
    () => false,
  );

export const destroyActiveComponent = () => {
  if (activeComponent) {
    activeComponent.destroy();
    activeComponent = null;
  }
  setPopupOpen(false);
};

// Note: Using any for TipTap internal types that are complex and not well exposed
interface SuggestionRenderReturn {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onStart: (props: any) => void | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdate: (props: any) => void;
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
  onExit: () => void;
}

export const suggestion = {
  items: ({ query, editor }: { query: string; editor: Editor }): VariableDef[] => {
    const variables = (editor.storage.variables as VariableDef[]) || [];

    const filtered = variables.filter((item: VariableDef) =>
      item.displayName.toLowerCase().includes(query.toLowerCase()),
    );
    return filtered;
  },

  render: (): SuggestionRenderReturn => {
    let component: ReactRenderer;
    let popup: HTMLDivElement;

    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onStart: (props: any) => {
        // Create close handler that also removes the @ trigger from the editor
        const handleClose = () => {
          if (props.editor && props.range) {
            props.editor.chain().focus().deleteRange(props.range).run();
          }
          destroyActiveComponent();
        };

        component = new ReactRenderer(VariableCommand, {
          props: {
            ...props,
            onClose: handleClose,
            selectArrays: !!props.editor?.storage?.selectArrays,
          },
          editor: props.editor,
        });

        activeComponent = component;
        setPopupOpen(true);

        popup = document.createElement("div");
        popup.className = "variable-suggestion-popup";
        popup.style.position = "fixed";
        popup.style.zIndex = "999999";
        popup.style.maxWidth = "320px";
        popup.style.width = "320px";
        popup.style.background = "white";
        popup.style.border = "1px solid #e5e7eb";
        popup.style.borderRadius = "0.5rem";
        popup.style.boxShadow =
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)";
        popup.style.maxHeight = "350px";
        popup.style.padding = "4px";
        popup.style.cursor = "default";
        popup.style.pointerEvents = "auto";
        popup.style.overflow = "hidden";

        // Position popup near the cursor
        const popupWidth = 320;
        let left = 10;

        const editorElement = props.editor?.view?.dom;
        const editorRect = editorElement?.getBoundingClientRect();
        const clientRect = props.clientRect?.();

        // Get cursor position from clientRect or fall back to editor position
        if (clientRect && clientRect.y > 0) {
          left = clientRect.x || clientRect.left;
        } else if (editorRect) {
          left = editorRect.left;
        }

        // Also check native selection for comparison
        let nativeSelectionRect: DOMRect | null = null;
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          nativeSelectionRect = range.getBoundingClientRect();
        }

        // Use native selection if available and valid, otherwise fall back to TipTap's clientRect
        const useNative =
          nativeSelectionRect && nativeSelectionRect.y > 0 && nativeSelectionRect.height > 0;
        const cursorRect = useNative ? nativeSelectionRect : clientRect;

        const cursorY = cursorRect?.y || cursorRect?.top || editorRect?.top || 0;
        const cursorBottom = cursorRect?.bottom || cursorY + (cursorRect?.height || 20);

        // Clamp horizontal position to viewport
        left = Math.max(10, Math.min(left, window.innerWidth - popupWidth - 10));

        // Check if popup fits below or above cursor (popup max height is 350px)
        const popupMaxHeight = 350;
        const spaceBelow = window.innerHeight - cursorBottom - 5;
        const spaceAbove = cursorY - 5;

        if (spaceBelow >= popupMaxHeight) {
          // Enough space below - position below cursor
          popup.style.top = `${cursorBottom + 5}px`;
          popup.style.bottom = "auto";
        } else if (spaceAbove >= popupMaxHeight) {
          // Enough space above - position above cursor using bottom
          const bottomValue = window.innerHeight - cursorY + 5;
          popup.style.bottom = `${bottomValue}px`;
          popup.style.top = "auto";
        } else {
          // Not enough space in either direction - default to below
          popup.style.top = `${cursorBottom + 5}px`;
          popup.style.bottom = "auto";
        }

        popup.style.left = `${left}px`;

        popup.appendChild(component.element);

        // Escape must close the popup without closing a surrounding modal.
        // Radix captures keydown on `document`, and it subscribes when the modal
        // opens — before this popup exists — so a document-level capture handler
        // here would still run second. `window` precedes `document` in the
        // capture path, which is what puts this handler first.
        const escapeHandler = (event: KeyboardEvent) => {
          if (event.key === "Escape") {
            // Stop ALL propagation to prevent modal from receiving this event
            event.stopImmediatePropagation();
            event.stopPropagation();
            event.preventDefault();
            // Forward to VariableCommand
            const ref = (component.ref as VariableCommandRef) || null;
            ref?.onKeyDown({ event } as SuggestionKeyDownProps);
          }
        };
        window.addEventListener("keydown", escapeHandler, true);
        // Store reference for cleanup
        (
          popup as HTMLDivElement & {
            __escapeHandler?: (e: KeyboardEvent) => void;
          }
        ).__escapeHandler = escapeHandler;

        // Add click handler to prevent clicks from bubbling to elements underneath
        popup.addEventListener("click", (event) => {
          event.stopPropagation();
          // Don't prevent default to allow CommandItem onSelect to work
        });

        // Add wheel handler to ensure wheel events can bubble up for scrolling
        popup.addEventListener(
          "wheel",
          () => {
            // Don't stop propagation - let wheel events bubble up to the modal
            // This ensures the modal can scroll even when the popup is visible
          },
          { passive: true },
        );

        // Always mount to document.body to avoid modal overflow issues
        document.body.appendChild(popup);

        const handleClickOutside = (event: MouseEvent) => {
          const target = event.target as Node;

          // Check if the click is on the popup or any of its children
          if (popup.contains(target) || popup === target) {
            return; // Don't close if clicking on the popup
          }

          // Check if the click is on the editor that triggered the suggestion
          const editorElement = props.editor.view.dom;
          if (editorElement.contains(target) || editorElement === target) {
            return; // Don't close if clicking on the editor
          }

          // Check if the click is on any input field in the modal
          // This prevents closing when clicking on other form inputs
          const targetElement = target as HTMLElement;
          if (
            targetElement &&
            (targetElement.tagName === "INPUT" ||
              targetElement.tagName === "TEXTAREA" ||
              targetElement.tagName === "SELECT" ||
              targetElement.closest("input, textarea, select"))
          ) {
            return; // Don't close if clicking on any form input
          }

          // Close the popup if clicking elsewhere
          popup.remove();
          document.removeEventListener("click", handleClickOutside);
        };

        // Add a longer delay to prevent immediate closure
        setTimeout(() => {
          document.addEventListener("click", handleClickOutside);
        }, 200);

        return null;
      },

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onUpdate(props: any) {
        component.updateProps(props);

        if (popup) {
          const popupWidth = 320;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          let left = 10;

          const editorElement = props.editor?.view?.dom;
          const editorRect = editorElement?.getBoundingClientRect();
          const clientRect = props.clientRect?.();

          // Get cursor position from clientRect or fall back to editor position
          if (clientRect && clientRect.y > 0) {
            left = clientRect.x || clientRect.left;
          } else if (editorRect) {
            left = editorRect.left;
          }

          // Also check native selection
          let nativeSelectionRect: DOMRect | null = null;
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            nativeSelectionRect = range.getBoundingClientRect();
          }

          const useNative =
            nativeSelectionRect && nativeSelectionRect.y > 0 && nativeSelectionRect.height > 0;
          const cursorRect = useNative ? nativeSelectionRect : clientRect;

          const cursorY = cursorRect?.y || cursorRect?.top || editorRect?.top || 0;
          const cursorBottom = cursorRect?.bottom || cursorY + (cursorRect?.height || 20);

          // Clamp horizontal position to viewport
          left = Math.max(10, Math.min(left, viewportWidth - popupWidth - 10));

          // Check if popup fits below or above cursor (popup max height is 350px)
          const popupMaxHeight = 350;
          const spaceBelow = viewportHeight - cursorBottom - 5;
          const spaceAbove = cursorY - 5;

          if (spaceBelow >= popupMaxHeight) {
            // Enough space below - position below cursor
            popup.style.top = `${cursorBottom + 5}px`;
            popup.style.bottom = "auto";
          } else if (spaceAbove >= popupMaxHeight) {
            // Enough space above - position above cursor using bottom
            const bottomValue = viewportHeight - cursorY + 5;
            popup.style.bottom = `${bottomValue}px`;
            popup.style.top = "auto";
          } else {
            // Not enough space in either direction - default to below
            popup.style.top = `${cursorBottom + 5}px`;
            popup.style.bottom = "auto";
          }

          popup.style.left = `${left}px`;
        }
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        // Let the VariableCommand handle all keys including Escape
        // It will call onClose when appropriate (e.g., at root level)
        const ref = (component.ref as VariableCommandRef) || null;
        return ref?.onKeyDown(props) ?? false;
      },

      onExit() {
        setPopupOpen(false);
        if (popup) {
          // Clean up escape handler
          const escapeHandler = (
            popup as HTMLDivElement & {
              __escapeHandler?: (event: KeyboardEvent) => void;
            }
          ).__escapeHandler;
          if (escapeHandler) {
            window.removeEventListener("keydown", escapeHandler, true);
          }
          popup.remove();
        }
        component.destroy();
        activeComponent = null;
      },
    };
  },
};

export default suggestion;
